import type { IconDefinition } from '@fortawesome/pro-regular-svg-icons';
import type { JSX } from 'react';
import React from 'react';

import type { EGoogleAdsKeywordMatchType } from '@feathr/blackbox';
import { Pill } from '@feathr/components';

import KeywordMatchTypePill from './KeywordMatchTypePill';

import * as styles from './KeywordPill.css';

interface IKeywordPillProps {
  text: string;
  /** Negative keywords carry no match type, so it isn't rendered for them. */
  matchType?: EGoogleAdsKeywordMatchType;
  /** Whether this keyword has a problem. A pill is not a field, so it can only tint. */
  hasError?: boolean;
  /** Trailing icon, for the editor's chips — the read-only lists have no action. */
  suffix?: IconDefinition;
}

/**
 * One keyword as a pill, with its match type as a status pill inside. Every keyword pill
 * carries the same height whether or not it shows a match type, so positive and negative
 * keywords sit level wherever the read-only summaries list them together.
 *
 * @param props - {@link IKeywordPillProps}.
 */
function KeywordPill({
  text,
  matchType,
  hasError = false,
  suffix,
}: Readonly<IKeywordPillProps>): JSX.Element {
  return (
    <Pill
      className={styles.root}
      showPrefix={false}
      suffix={suffix}
      theme={hasError ? 'red' : 'neutral'}
      truncate={false}
    >
      {text}
      {!!matchType && <KeywordMatchTypePill matchType={matchType} />}
    </Pill>
  );
}

export default KeywordPill;
